import React, { useState } from "react";
import { Alert, Input, InputNumber, Segmented, Slider, Typography } from "antd";
import { siteCentre } from "../utils/geoAlign";
import { formatSize, sizeChanged } from "../utils/sizeChange";

// The sidebar of 對齊地圖: one body per step, then the display options, the
// live readouts and the size alert, which stay put across steps.

export const EMPTY_HINT =
  "貼上座標（Google 地圖按右鍵即可複製），或直接在地圖上點選場域中心";

const INK_SECONDARY = "#595959";
const section = { display: "flex", flexDirection: "column", gap: 8 };
const label = { fontSize: 12, color: INK_SECONDARY };
const help = { fontSize: 12, color: INK_SECONDARY, margin: 0 };

const Heading = ({ children }) => (
  <Typography.Text strong style={{ fontSize: 13 }}>
    {children}
  </Typography.Text>
);

/** A number that commits on Enter or blur, so typing never jumps the site. */
const NumberField = ({ name, value, onCommit, ...rest }) => {
  const [draft, setDraft] = useState(undefined);
  const commit = () => {
    if (typeof draft === "number" && Number.isFinite(draft)) onCommit(draft);
    setDraft(undefined);
  };
  return (
    <label style={{ ...section, gap: 2 }}>
      <span style={label}>{name}</span>
      <InputNumber
        aria-label={name}
        style={{ width: "100%" }}
        value={draft === undefined ? value : draft}
        onChange={setDraft}
        onBlur={commit}
        onPressEnter={commit}
        {...rest}
      />
    </label>
  );
};

export const FindStep = ({ align }) => (
  <div style={section}>
    <Heading>場域中心座標</Heading>
    <Input.Search
      placeholder="例如 24.147731, 120.673648"
      aria-label="場域中心座標"
      enterButton="前往"
      value={align.paste}
      status={align.pasteError ? "error" : undefined}
      onChange={(e) => align.setPaste(e.target.value)}
      onSearch={align.jump}
    />
    {align.pasteError && (
      <Alert type="error" showIcon message={align.pasteError} />
    )}
    <p style={help}>
      在 Google
      地圖上對場域中心按右鍵，點第一行的座標即可複製。也可以直接在地圖上點選。
    </p>
  </div>
);

export const AlignStep = () => (
  <div style={section}>
    <Heading>拖曳／旋轉／縮放</Heading>
    <p style={help}>
      拖曳場域移動，拖曳上方圓點旋轉。拖曳四角等比縮放，拖曳四邊中點只改寬或高，都以中心為準。
    </p>
    <p style={help}>
      點一下地圖後：方向鍵移動 0.1 m（Shift 1 m），[ ] 旋轉 0.1°（Shift
      1°），- = 縮放 0.1 %（Shift 1 %）。
    </p>
  </div>
);

export const Readouts = ({ siteRef, floor, align }) => {
  const [lat, lng] = siteCentre(siteRef, floor);
  return (
    <div style={section}>
      <Heading>目前位置</Heading>
      <NumberField
        name="中心緯度"
        value={lat}
        precision={7}
        step={0.000001}
        min={-90}
        max={90}
        onCommit={(v) => align.setCentre(v, lng)}
      />
      <NumberField
        name="中心經度"
        value={lng}
        precision={7}
        step={0.000001}
        min={-180}
        max={180}
        onCommit={(v) => align.setCentre(lat, v)}
      />
      <NumberField
        name="角度（北起順時針）"
        value={siteRef.rotationDeg}
        precision={2}
        step={0.1}
        min={0}
        max={360}
        addonAfter="°"
        onCommit={align.setAngle}
      />
      <div data-testid="geo-size-readout" style={{ ...section, gap: 2 }}>
        <span style={label}>尺寸</span>
        <span style={{ fontVariantNumeric: "tabular-nums" }}>
          {formatSize(floor)} m
        </span>
      </div>
    </div>
  );
};

/** The one size message: only when 保存 would change the saved size. */
export const SizeAlert = ({ saved, floor }) =>
  sizeChanged(saved, floor) ? (
    <Alert
      data-testid="geo-size-alert"
      type="info"
      showIcon
      message={`場域尺寸將變更：${formatSize(saved)} → ${formatSize(floor)} m（按保存生效）`}
    />
  ) : null;

export const DisplayOptions = ({
  opacity,
  onOpacity,
  tileLayers,
  layerId,
  onLayer,
}) => (
  <div style={section}>
    <Heading>顯示</Heading>
    {tileLayers.length > 1 && (
      <Segmented
        block
        size="small"
        value={layerId}
        onChange={onLayer}
        options={tileLayers.map((l) => ({ label: l.name, value: l.id }))}
      />
    )}
    <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
      <span style={label}>不透明度</span>
      <Slider
        ariaLabelForHandle="平面圖不透明度"
        style={{ flex: 1, margin: "6px 4px" }}
        min={10}
        max={100}
        step={5}
        value={Math.round(opacity * 100)}
        tooltip={{ formatter: (v) => `${v}%` }}
        onChange={(v) => onOpacity(v / 100)}
      />
    </div>
  </div>
);
